import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import async from 'async';
import { Link, withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { Helmet } from 'react-helmet';
import pluralize from 'pluralize';
import { Alert } from 'react-bootstrap';
import { getStatus } from '../../../actions/deviceActions';
import { loadPlaylists, deletePlaylist } from '../../../actions/playlistActions';
import {
  loadContentItems,
  fetchContentItems,
  contentItemsUnselectAll,
  loadAllContentItems,
} from '../../../actions/contentItemActions';
import { deleteSign, getSigns } from '../../../actions/signActions';
import './Home.css';
import AddLocationDialog from '../../../components/Locations/AddLocationDialog';
import { addFolder, loadFolders, deleteFolder } from '../../../actions/folderActions';
import { addNotification } from '../../../actions/notificationActions';
import { getParentPath } from '../../../utils/parent';
import errorMessage from '../../../utils/errorMessage';
import DeviceMapPanel from '../../../components/Maps/DeviceMapPanel';
import RecentUploads from '../../../components/LatestUploads/LatestUploads';
import { getCampaignSchedule } from '../../../actions/campaignScheduleActions';
import ShortHandlers from '../../../components/ShortHandlers/ShortHandlers';
import Badges from '../../../components/Badges/Badges';
import Header from '../../../components/Header/Header';
import {
  queueUploads,
  uploadAsset,
  uploadRemoveItem,
  getEncodingQueue,
  deleteAsset,
  getAssets,
} from '../../../actions/assetActions';
import { deleteSchedule } from '../../../actions/scheduleActions';
import Confirm from '../../../components/Confirm/Confirm';
import UploadPrompt from '../../../components/UploadPrompt/UploadPrompt';
import { addSupportedAttr, isUnixHiddenPath } from '../../../utils/file';
import AssetsUploads from '../../../components/Assets/AssetsUploads';
import { deleteSequence, loadSequences } from '../../../actions/sequenceActions';
import { deleteCampaign } from '../../../actions/campaignActions';
import ConfirmModal from '../../../components/ConfirmActions/ConfirmActions';
import { getDeleteFolderComponent } from '../../../components/ContentHelper/ContentHelper';

function Home(props) {
  const { loadPlaylists, getAssets, getSigns, loadFolders, loadSequences, getCampaignSchedule } =
    props;

  useEffect(() => {
    getCampaignSchedule();
    loadPlaylists('?count=100000000');
    getAssets('?count=100000000');
    getSigns('?count=100000000');
    loadFolders('?count=100000000');
    loadSequences('?count=100000000');
  }, []);

  const [showFolderDialog, setShowFolderDialog] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [deepDelete, setDeepDelete] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmModalTitle, setConfirmModalTitle] = useState('');
  const [confirmModalOnClick, setConfirmModalOnClick] = useState(() => () => {});
  const [confirmModalSubtitle, setConfirmModalSubtitle] = useState('');
  const [confirmModalBtnText, setConfirmModalBtnText] = useState('');
  const [deleteFolderComponent, setDeleteFolderComponent] = useState(false);
  const [mapColorMode, setMapColorMode] = useState('status');
  const refWarning = useRef();
  const {
    assets: { encodeTypes },
  } = props;
  let encodeType = {};

  const handleAddFolder = folder => {
    const {
      location: { search },
      addNotification,
      loadFolders,
      loadContentItems,
    } = props;

    setShowFolderDialog(false);
    folder.parent = getParentPath(props);
    addFolder(folder).then(
      () => {
        addNotification({ type: 'success', text: 'Folder created.' });
        loadFolders('?count=100000000');
        loadContentItems(search);
      },
      err => addNotification({ type: 'danger', text: errorMessage(err) }),
    );
  };

  const filterUploadingFiles = allFiles =>
    addSupportedAttr(allFiles.filter(item => !isUnixHiddenPath(item.webkitRelativePath)));

  const getUploadingFolders = files => {
    const folders = [];

    files
      .filter(item => item.webkitRelativePath)
      .forEach(item => {
        const parents = item.webkitRelativePath.slice(0, -item.name.length - 1).split('/');
        let fullPath = '';

        parents.forEach(parent => {
          fullPath += `${parent}/`;

          if (!folders.includes(fullPath)) {
            folders.push(fullPath);
          }
        });
      });

    return folders;
  };

  const getConflictedUploadingFolders = uploadingFolders => {
    const {
      folders: {
        folders: { folders },
      },
    } = props;
    const parentPath = getParentPath(props);
    const rootFolders = [];

    uploadingFolders.forEach(item => {
      const folder = item.split('/')[0];

      if (!rootFolders.includes(folder)) {
        rootFolders.push(folder);
      }
    });

    return folders
      .filter(item => item.parent === parentPath && rootFolders.includes(item.name))
      .map(item => item.name);
  };

  const hideUploadDialog = () => setShowUploadDialog(false);

  const uploadAssets = (allFiles, _encodeType) => {
    const { addNotification } = props;
    const files = filterUploadingFiles(allFiles);
    const folders = getUploadingFolders(files);
    const conflicts = getConflictedUploadingFolders(folders);

    hideUploadDialog();

    if (conflicts.length) {
      addNotification({
        type: 'danger',
        text: `Folders exist: ${conflicts.join(', ')}`,
      });

      return;
    }

    encodeType = _encodeType;
    addUploadingFolders(folders, files);
  };

  const addUploadingFolders = (items, files) => {
    const { loadFolders, loadContentItems, addNotification } = props;
    const parentPath = getParentPath(props);

    if (!items.length) {
      loadFolders('?count=100000000');
      loadContentItems('?parent=/&sort=createdDate+desc&count=10');
      uploadStart(files);

      return;
    }

    const item = items.shift();
    const parents = item.split('/').filter(Boolean);
    const name = parents.pop();
    let parent = parentPath + parents.join('/');

    if (!parent.endsWith('/')) {
      parent += '/';
    }

    return addFolder({ name, parent }).then(
      () => {
        addUploadingFolders(items, files);
        addNotification({ type: 'success', text: `Folder created: ${parent}${name}` });
      },
      err => addNotification({ type: 'danger', text: errorMessage(err) }),
    );
  };

  const uploadStart = _files => {
    const { queueUploads } = props;

    if (!_files.length) {
      return;
    }

    const files = _files.map(file => {
      file.id = Math.floor(Math.random() * Number.MAX_SAFE_INTEGER);
      file.parent =
        getParentPath(props) + (file.webkitRelativePath || '').slice(0, -file.name.length);
      file.encodeType = encodeType;

      return file;
    });

    window.SignStix.uploadingFiles.push(...files);
    queueUploads(files);
    const limit = Math.min(window.SignStix.uploadingFiles.length, window.SignStix.uploadingLimit);

    while (window.SignStix.uploading < limit) {
      uploadNext();
    }
  };

  const uploadNext = () => {
    const { uploadAsset } = props;

    if (!window.SignStix.uploadingFiles.length) {
      return;
    }

    const file = window.SignStix.uploadingFiles.shift();

    uploadAsset(file, uploadNext);
  };

  const { assets, uploadRemoveItem, getEncodingQueue } = props;
  const { uploading, encodingQueue } = assets;

  const deleteItems = contentItems => {
    const {
      addNotification,
      contentItemsUnselectAll,
      loadFolders,
      loadContentItems,
      loadAllContentItems,
    } = props;
    let deleteMessage = '';
    let failureMessage = '';

    async.eachSeries(
      contentItems,
      (contentItem, callback) => {
        deleteMessage = `${contentItem.type}: ${contentItem.name} has been deleted.`;
        failureMessage = `${contentItem.type}: ${contentItem.name} could not be deleted.`;
        deleteItem(contentItem).then(
          res => {
            addNotification({ type: 'success', text: deleteMessage });
            callback(null, res);
          },
          () => {
            addNotification({ type: 'danger', text: failureMessage });
            callback(null, false);
          },
        );
      },
      () => {
        loadFolders('?parent=/&sort=createdDate+desc&count=10');
        loadContentItems('?parent=/&sort=createdDate+desc&count=10');
        setDeepDelete(false);
        loadAllContentItems();
        contentItemsUnselectAll();
      },
    );
  };

  const deleteItem = item => {
    switch (item.type) {
      case 'image':
      case 'video':
      case 'file':
        return deleteAsset(item.fileId);
      case 'playlist':
        return deletePlaylist(item.content_id);
      case 'sign':
        return deleteSign(item.content_id);
      case 'sequence':
        return deleteSequence(item.content_id);
      case 'schedule':
        return deleteSchedule(item.content_id);
      case 'campaign':
        return deleteCampaign(item.content_id);
      case 'folder':
        return deleteFolder(item.content_id, item.parent);
      default:
        return null;
    }
  };

  const showFailedDeleteWarning = items => {
    const total =
      items.foldersInUse.length +
      items.imagesInUse.length +
      items.playlistsInUse.length +
      items.campaignsInUse.length;

    if (!total) {
      return;
    }

    const body = (
      <div>
        <p>
          {pluralize('items', items.itemsToDelete.length, true)}{' '}
          {pluralize('is', items.itemsToDelete.length)} deleted.
        </p>
        <Alert bsStyle="warning">
          {pluralize('item', total, true)} cannot be deleted because the {pluralize('item', total)}{' '}
          {pluralize('is', total)} in use:
        </Alert>
        <ul>
          {items.foldersInUse.map(item => (
            <li key={item.id}>
              <Link to={`/folders/${item.id}`} target="_blank">
                {item.name}
              </Link>
              <span className="text-light"> [folder]</span> used in{' '}
              {pluralize(
                'sign',
                new Set(item.usedInSigns.map(sign => sign.split('-')[0])).size,
                true,
              )}
            </li>
          ))}
          {items.imagesInUse.map(item => (
            <li key={item.id}>
              <Link to={`/assets/${item.id}`} target="_blank">
                {item.name}
              </Link>
              <span className="text-light"> [image]</span> used in{' '}
              {pluralize(
                'sign',
                new Set(item.usedInSigns.map(sign => sign.split('-')[0])).size,
                true,
              )}
            </li>
          ))}
          {items.playlistsInUse.map(item => (
            <li key={item.id}>
              <Link to={`/playlists/${item.id}`} target="_blank">
                {item.name}
              </Link>
              <span className="text-light"> [playlist]</span> used in{' '}
              {pluralize(
                'sign',
                new Set(item.usedInSigns.map(sign => sign.split('-')[0])).size,
                true,
              )}
            </li>
          ))}
          {items.campaignsInUse.map(item => (
            <li key={item.campaignId}>
              <Link to={`/campaigns/${item.campaignId}`} target="_blank">
                {item.campaignName}
              </Link>
              <span className="text-light"> [campaign]</span> used in{' '}
              <Link to="/campaign-schedule">Campaign Schedule</Link>
            </li>
          ))}
          {items.videosInUse.map(item => (
            <li key={item.id}>
              <Link to={`/assets/${item.id}`} target="_blank">
                {item.name}
              </Link>
              <span className="text-light"> [video]</span> used in{' '}
              {pluralize('playlist', item.usedInPlaylists.length, true)}
            </li>
          ))}
        </ul>
      </div>
    );

    refWarning.current.show('Delete Warning', body);
  };

  const getFilteredItemsForDeletion = items => {
    const { folders, assets, playlists, campaignSchedule } = props;
    const itemsToDelete = [];
    const foldersInUse = [];
    const imagesInUse = [];
    const playlistsInUse = [];
    const campaignsInUse = [];
    const videosInUse = [];
    let currItem;
    let used;

    items.forEach(item => {
      used = false;

      if (item.type === 'folder') {
        currItem = folders.folders.folders.find(folder => +folder.id === +item.content_id);

        if (currItem && Boolean(currItem.usedInSigns[0])) {
          foldersInUse.push(currItem);
          used = true;
        }
      } else if (item.type === 'image') {
        currItem = assets.assets.assets.find(asset => asset.id === item.fileId);

        if (currItem && Boolean(currItem.usedInSigns[0])) {
          imagesInUse.push(currItem);
          used = true;
        }
      } else if (item.type === 'video') {
        currItem = assets.assets.assets.find(asset => asset.id === item.fileId);

        if (currItem && Boolean(currItem.usedInPlaylists[0])) {
          videosInUse.push(currItem);
          used = true;
        }
      } else if (item.type === 'playlist') {
        currItem = playlists.playlists.playlists.find(playlist => playlist.id === item.content_id);

        if (currItem && Boolean(currItem.usedInSigns[0])) {
          playlistsInUse.push(currItem);
          used = true;
        }
      } else if (item.type === 'campaign') {
        currItem = campaignSchedule.campaignSchedule.campaignScheduleEntries.find(
          entry => entry.campaignId === item.content_id,
        );

        if (currItem) {
          campaignsInUse.push(currItem);
          used = true;
        }
      }

      if (!used) {
        itemsToDelete.push(item);
      }
    });

    return {
      itemsToDelete,
      foldersInUse,
      imagesInUse,
      playlistsInUse,
      campaignsInUse,
      videosInUse,
    };
  };

  const mGetItemsForDeletion = (allContentItems, selectedItems, deleteAllContent) => {
    const {
      contentItems: { contentItems },
    } = props;
    const items = [];
    const contentItemsMap = {};

    contentItems.content_items.forEach(el => {
      contentItemsMap[el.content_id] = el;
    });

    if (deleteAllContent) {
      selectedItems.forEach(selectedItem => {
        if (selectedItem.type === 'folder') {
          const folderPath = `${selectedItem.parent + selectedItem.name}/`;

          allContentItems.content_items.forEach(item => {
            if (item.parent.startsWith(folderPath)) {
              items.push(item);
            }
          });

          items.push(contentItemsMap[selectedItem.content_id]);
        } else {
          items.push(contentItemsMap[selectedItem.content_id]);
        }
      });
    } else {
      selectedItems.forEach(selectedItem => items.push(contentItemsMap[selectedItem.content_id]));
    }

    return items;
  };

  const DeleteFolderContents = getDeleteFolderComponent(setDeepDelete, deepDelete);

  const prepareDeleteFunction = ({ title, subtitle, content }) => {
    setConfirmModalTitle(title);
    setConfirmModalSubtitle(subtitle);
    setConfirmModalOnClick(() => deleteAllContent => {
      fetchContentItems('?count=100000000').then(res => {
        const itemsForDeletion = mGetItemsForDeletion(res, [content], deleteAllContent);
        const items = getFilteredItemsForDeletion(itemsForDeletion);

        showFailedDeleteWarning(items);
        deleteItems(items.itemsToDelete);
      });
    });

    if (content.type === 'folder') {
      setDeleteFolderComponent(true);
    }

    setConfirmModalBtnText('Delete Item');
    setShowConfirmModal(true);
  };

  return (
    <div className="min-h-screen bg-[#EFF5F8] pb-16">
      <Header />
      <Helmet title="Home" />
      <AssetsUploads
        uploading={uploading}
        uploadRemoveItem={uploadRemoveItem}
        encodingQueue={encodingQueue}
        getEncodingQueue={getEncodingQueue}
      />
      <div className="mt-8 relative flex items-center justify-center !bg-transparent pb-2">
        <img
          src="/assets/images/icons/home_display_image.jpg"
          alt="home display"
          className="w-full  rounded-[15px] "
        />

        <img
          src="/assets/images/icons/signstix_white_signin_logo.svg"
          alt="device_logo"
          className="md:w-[302px] md:h-[86px]  absolute md:top-[50%]  md:left-[16px] top-[0.4px] bottom-[8px]  left-[8px]  w-[150px] h-[50px] md:-translate-y-1/2"
        />
      </div>

      <div className="mt-8">
        <ShortHandlers
          setShowFolderDialog={setShowFolderDialog}
          setShowUploadDialog={setShowUploadDialog}
        />
      </div>

      <div className="mt-8">
        <Badges />
      </div>

      <div className="mt-8">
        <div className="md:flex  md:space-x-4 grid grid-cols-1  gap-y-2">
          <div className=" w-[100%] md:w-[50%]">
            <RecentUploads
              deleteItems={deleteItems}
              prepareDeleteFunction={prepareDeleteFunction}
            />
          </div>
          <div className="flex-1 w-[100%] md:w-[50%] h-[358px]">
            <DeviceMapPanel
              className="h-full"
              colorMode={mapColorMode}
              onColorModeChange={setMapColorMode}
              dataUrl="/cms/data/v1/devices/map"
              pollIntervalMs={15000}
              showSummary
            />
          </div>
        </div>
      </div>
      <AddLocationDialog
        show={showFolderDialog}
        onHide={() => setShowFolderDialog(false)}
        type="folder"
        onSuccess={handleAddFolder}
        locations={[]}
        parent=""
      />
      <UploadPrompt
        show={showUploadDialog}
        onHide={hideUploadDialog}
        title="Upload Assets"
        encodeTypes={encodeTypes}
        onSuccess={uploadAssets}
        multiple
      />

      <Confirm ref={refWarning} />
      <ConfirmModal
        show={showConfirmModal}
        title={confirmModalTitle}
        onClick={() => confirmModalOnClick(deepDelete)}
        subtitle={deleteFolderComponent ? '' : confirmModalSubtitle}
        setShowConfirmModal={setShowConfirmModal}
        data={[]}
        confirmModalBtnText={confirmModalBtnText}
        additionalComponent={deleteFolderComponent ? DeleteFolderContents : null}
        deepDelete={deepDelete}
        onCancelClick={() => {
          setDeleteFolderComponent(false);
          setDeepDelete(false);
        }}
      />
    </div>
  );
}

Home.propTypes = {
  loadPlaylists: PropTypes.func.isRequired,
  contentItems: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  addNotification: PropTypes.func.isRequired,
  loadFolders: PropTypes.func.isRequired,
  loadContentItems: PropTypes.func.isRequired,
  uploadAsset: PropTypes.func.isRequired,
  queueUploads: PropTypes.func.isRequired,
  folders: PropTypes.object.isRequired,
  assets: PropTypes.object.isRequired,
  uploadRemoveItem: PropTypes.func.isRequired,
  getEncodingQueue: PropTypes.func.isRequired,
  playlists: PropTypes.object.isRequired,
  getSigns: PropTypes.func.isRequired,
  getAssets: PropTypes.func.isRequired,
  campaignSchedule: PropTypes.object.isRequired,
  contentItemsUnselectAll: PropTypes.func.isRequired,
  loadAllContentItems: PropTypes.func.isRequired,
  getCampaignSchedule: PropTypes.func.isRequired,
  loadSequences: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
  auth: state.auth,
  assets: state.assets,
  previousLocation: state.app.previousLocation,
  locations: state.locations,
  roles: state.roles,
  users: state.users,
  playlists: state.playlists,
  contentItems: state.contentItems,
  devices: state.devices,
  loadFolders: PropTypes.func.isRequired,
  loadContentItems: PropTypes.func.isRequired,
  folders: state.folders,
  campaignSchedule: state.campaignSchedule,
});
const mapDispatchToProps = {
  loadPlaylists,
  getStatus,
  addNotification,
  loadFolders,
  loadContentItems,
  uploadAsset,
  queueUploads,
  uploadRemoveItem,
  getEncodingQueue,
  contentItemsUnselectAll,
  loadAllContentItems,
  getSigns,
  getAssets,
  loadSequences,
  getCampaignSchedule,
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Home));
